import { useMemo } from 'react';
import { translate } from '../../utils/translator';
// Import step icons
import TextStep1Icon from '../../icons/start-consultation/text/step-1.svg';
import TextStep2Icon from '../../icons/start-consultation/text/step-2.svg';
import TextStep3Icon from '../../icons/start-consultation/text/step-3.svg';
import VideoStep1Icon from '../../icons/start-consultation/video/step-1.svg';
import VideoStep2Icon from '../../icons/start-consultation/video/step-2.svg';
import VideoStep3Icon from '../../icons/start-consultation/video/step-3.svg';

interface ProcessFlowProps {
  consultationType: 'text' | 'video'; 
  language?: string;
}

function ProcessFlow({ consultationType, language='de' }: ProcessFlowProps) {

  const steps = useMemo(() => {
    return [
      {
        number: 1,
        title:  translate(language, "start_consultation", `processFlow/${consultationType}/step_1/title`),
        description:  translate(language, "start_consultation", `processFlow/${consultationType}/step_1/description`),
        icon: consultationType === 'text' ? TextStep1Icon : VideoStep1Icon
      },
      {
        number: 2,
        title: translate(language, "start_consultation", `processFlow/${consultationType}/step_2/title`),
        description: translate(language, "start_consultation", `processFlow/${consultationType}/step_2/description`),
        icon: consultationType === 'text' ? TextStep2Icon : VideoStep2Icon
      },
      {
        number: 3,
        title: translate(language, "start_consultation", `processFlow/${consultationType}/step_3/title`),
        description: translate(language, "start_consultation", `processFlow/${consultationType}/step_3/description`),
        icon: consultationType === 'text' ? TextStep3Icon : VideoStep3Icon
      }
    ]
  }, [consultationType, language]);


  return (
    <div
      id="process-flow-container"
      className="
        grid
        grid-cols-[auto_1fr]
        md:h-full
        gap-x-[30px]
        md:gap-y-[14px] gap-y-[10px]
        relative"
      style={{
        gridTemplateRows: `repeat(${steps.length},1fr)`,
      }}
    >
      {/* steps connectors aka vertical Hyphen  ("-") between the steps*/}
      <div
        id="steps-connector"
        className="
          self-start md:self-auto
          mt-[28px]
          justify-self-center 
          row-span-full 
          col-start-1 min-h-24
          w-[2px] 
          bg-[repeating-linear-gradient(to_bottom,rgb(81,180,71)_0px,rgb(81,180,71)_4px,transparent_4px,transparent_8px)]"
          style={{
          height: `calc(100% / ${steps.length} * ${steps.length - 1})`,
        }}
      >
      </div>

      {steps.map((step, index) => (
        <div 
          key={index}  
          className="contents"
        >
          <div
            id="process-flow-step-img-container"
            className={`
                relative
                md:w-[56px] w-[52px]
                md:h-[56px] h-[52px]
                col-start-1 
                col-end-2
                row-start-${index + 1} 
                row-end-${index + 2}
                self-start md:self-auto
              `}
          >
            <div
              id="process-flow-step-img"
              className="
                  w-full
                  h-full
                  bg-[#C2D2BE]
                  rounded-full
                  flex
                  items-center
                  justify-center
                "
            >
              {(() => {
                const iconSrc = step.icon;
                return iconSrc ? (
                  <img
                    src={iconSrc}
                    alt={`Step ${step.number} icon`}
                    className="w-[60%] h-[60%] object-contain"
                  />
                ) : null;
              })()}
            </div>

            <div
              id="process-flow-step-number"
              className="
                  absolute
                  bottom-0
                  right-0
                  translate-x-[20%]
                  translate-y-0
                  w-[15px]
                  h-[15px]
                  bg-[linear-gradient(to_bottom_right,#10947D,#91BC28)]
                  rounded-full
                  text-white
                  flex items-center justify-center
                  font-medium
                  text-[10px]
                "
            >
              {step.number}
            </div>
          </div>

          <div
            id="process-flow-step-title-and-description"
            className={`
                col-start-2 
                col-end-3
                h-full
                row-start-${index + 1} 
                row-end-${index + 2}
                md:pb-0 
                self-start md:self-auto
                ${index !== steps.length - 1
                ? "md:border-0 border-b-[2px] [border-bottom-style:solid] border-[#F5F7F5] pb-[12px]"
                : ""
              }
              `}
          >
            <h3
              id="process-flow-step-title"
              className="
                  font-semibold 
                  md:text-[18px] text-[12px] 
                  leading-[1.5]
                  font-sans-serif
                  text-black
                  m-0
                "
            >
              {step.title}
            </h3>

            <p
              id="process-flow-step-description"
              className={`
                  md:text-[14px] text-[12px] 
                  font-normal 
                  leading-[1.3] 
                  font-sans-serif
                  text-black
                  m-0
                  ${index === steps.length - 1 && consultationType === 'video' ? 'pb-[16px] md:pb-0' : ''}
                `}
            >
              {step.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ProcessFlow;
